export const MIN_EXPIRATION_MONTHS = 1;
export const MAX_EXPIRATION_MONTHS = 24;

export const SIGNED_TRANSACTION_TYPES = ["ADJUST", "REVERSAL"];

export const BATCH_STATUS = {
  ACTIVE: "ACTIVE",
  EXPIRED: "EXPIRED",
  REDEEMED: "REDEEMED",
  REVOKED: "REVOKED",
  NO_EXPIRATION: "NO_EXPIRATION"
};

export const BATCH_STATUS_DISPLAY = {
  ACTIVE: { text: "ใช้งานได้", variant: "success" },
  EXPIRED: { text: "หมดอายุ", variant: "danger" },
  REDEEMED: { text: "แลกครบแล้ว", variant: "secondary" },
  REVOKED: { text: "ถูกยกเลิก", variant: "dark" },
  NO_EXPIRATION: { text: "ไม่มีวันหมดอายุ", variant: "info" }
};

export const TRANSACTION_TYPE_DISPLAY = {
  EARN: { text: "ได้รับแต้ม", variant: "success" },
  REDEEM: { text: "แลกแต้ม", variant: "primary" },
  EXPIRE: { text: "แต้มหมดอายุ", variant: "danger" },
  ADJUST: { text: "ปรับแต้ม", variant: "warning" },
  REVERSAL: { text: "ยกเลิกรายการแต้ม", variant: "dark" }
};

export const POINT_SOURCE_DISPLAY = {
  receipt: { text: "ใบเสร็จ", variant: "light-primary" },
  point_grant: { text: "Staff แจกแต้ม", variant: "light-warning" },
  qr_scan: { text: "สแกน QR", variant: "light-success" },
  referral: { text: "แนะนำเพื่อน", variant: "light-info" },
  manual_adjust: { text: "ผู้ดูแลปรับแต้ม", variant: "light-dark" }
};

const UNKNOWN_POINT_SOURCE_DISPLAY = { text: "อื่นๆ", variant: "light" };

const REFERRAL_ROLE_LABEL = {
  INVITER: "ชวนเพื่อน",
  INVITEE: "ถูกชวนโดย"
};

export function pointSourceDisplay(sourceType) {
  return POINT_SOURCE_DISPLAY[sourceType] || UNKNOWN_POINT_SOURCE_DISPLAY;
}

export function resolvePointSourceType(batch) {
  return batch.source ? batch.source.type : batch.source_type;
}

export function pointSourceDetails(source) {
  const details = [
    { label: "แคมเปญ", value: source.campaign_name },
    { label: "ประเภทใบเสร็จ", value: source.receipt_type_name },
    { label: "เลขที่ใบเสร็จ", value: source.receipt_number },
    {
      label: "ยอดซื้อ",
      value:
        source.total_amount === null ? null : `${formatAmount(source.total_amount)} บาท`
    },
    { label: "พนักงานที่แจกแต้ม", value: source.staff_name },
    {
      label: "QR",
      value: source.qr_name && `${source.qr_name} (${source.qr_code})`
    },
    {
      label: REFERRAL_ROLE_LABEL[source.referral_role],
      value: source.referral_customer_name
    }
  ];
  return details.filter((detail) => detail.label && detail.value);
}

function formatAmount(value) {
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

const DATE_ONLY_LENGTH = 10;

export function parseDateOnly(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime())
      ? null
      : new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const text = String(value);
  if (text.length > DATE_ONLY_LENGTH) return parseDateOnly(new Date(text));
  const [year, month, day] = text.split("-").map(Number);
  return year && month && day ? new Date(year, month - 1, day) : null;
}

export function calculateExpirationDate(receivedDate, expirationMonths) {
  const received = parseDateOnly(receivedDate);
  const months = Number(expirationMonths);
  if (!received || !Number.isInteger(months) || months < 1) return null;
  return new Date(received.getFullYear(), received.getMonth() + months + 1, 0);
}

export function validateExpirationMonths(value) {
  if (value === null || value === undefined || value === "") {
    return "กรุณาระบุระยะเวลาหมดอายุ";
  }
  const months = Number(value);
  if (!Number.isInteger(months) || months < MIN_EXPIRATION_MONTHS) {
    return `ระยะเวลาหมดอายุต้องเป็นจำนวนเต็มตั้งแต่ ${MIN_EXPIRATION_MONTHS} เดือนขึ้นไป`;
  }
  if (months > MAX_EXPIRATION_MONTHS) {
    return `ระยะเวลาหมดอายุต้องไม่เกิน ${MAX_EXPIRATION_MONTHS} เดือน`;
  }
  return null;
}

export function describePointLifetime(config) {
  return config.is_enabled
    ? `หมดอายุใน ${config.expiration_months} เดือน`
    : "ไม่มีวันหมดอายุ";
}

export function resolveBatchStatus(batch, today = new Date()) {
  if (batch.ledger_status === BATCH_STATUS.REVOKED) {
    return BATCH_STATUS.REVOKED;
  }
  if (Number(batch.expired_amount) > 0) return BATCH_STATUS.EXPIRED;
  if (remainingPoint(batch) <= 0) return BATCH_STATUS.REDEEMED;
  if (!batch.expiration_date) return BATCH_STATUS.NO_EXPIRATION;
  if (parseDateOnly(batch.expiration_date) < parseDateOnly(today)) {
    return BATCH_STATUS.EXPIRED;
  }
  return BATCH_STATUS.ACTIVE;
}

export function remainingPoint(batch) {
  if (batch.ledger_status === BATCH_STATUS.REVOKED) return 0;
  return Math.max(
    0,
    Number(batch.earned_amount) -
      Number(batch.redeemed_amount) -
      Number(batch.expired_amount) -
      Number(batch.adjusted_amount) -
      Number(batch.reversed_amount)
  );
}

export function formatThaiDate(value) {
  const date = parseDateOnly(value);
  if (!date) return "-";
  return date.toLocaleDateString("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}

export function formatThaiDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleString("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

export function formatPoint(value) {
  return Number(value || 0).toLocaleString();
}

export function formatSignedPoint(value) {
  return `${Number(value) > 0 ? "+" : ""}${formatPoint(value)}`;
}
